import { mapValues, get, isEmpty, keys, omitBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import camelCase from 'lodash/camelCase';

import { PROFILE_FIELDS_TYPES } from '../../../../constants/formFields';
import {
  makeRequiredFieldsValidator,
  useFormState,
} from '../../../../hocs/useFormWithValidation';
import { VALIDATION_ERRORS } from '../../../../constants/strings';

const commonInitialState = {
  [PROFILE_FIELDS_TYPES.FIRST_NAME]: '',
  [PROFILE_FIELDS_TYPES.LAST_NAME]: '',
  [PROFILE_FIELDS_TYPES.DOB]: '',
  [PROFILE_FIELDS_TYPES.HOME_PHONE]: '',
};

export const extraInitialState = {
  [PROFILE_FIELDS_TYPES.GENDER]: '',
  [PROFILE_FIELDS_TYPES.ADDRESS_1]: '',
  [PROFILE_FIELDS_TYPES.ADDRESS_2]: '',
  [PROFILE_FIELDS_TYPES.CITY]: '',
  [PROFILE_FIELDS_TYPES.STATE]: '',
  [PROFILE_FIELDS_TYPES.POSTAL_CODE]: '',
  [PROFILE_FIELDS_TYPES.COUNTRY]: '',
};

export const additionalState = {
  [PROFILE_FIELDS_TYPES.RACE]: '',
  [PROFILE_FIELDS_TYPES.ETHNICITY]: '',
  [PROFILE_FIELDS_TYPES.LANGUAGE]: '',
};

const getInitialState = (isInlineVisitType, isEirVisitType) => {
  const additionalVisitType = isEirVisitType ? additionalState : {};

  return isInlineVisitType
    ? commonInitialState
    : { ...commonInitialState, ...extraInitialState, ...additionalVisitType };
};

const msg = {
  [PROFILE_FIELDS_TYPES.POSTAL_CODE]:
    VALIDATION_ERRORS.profile.postalCode.format,
  [PROFILE_FIELDS_TYPES.HOME_PHONE]:
    VALIDATION_ERRORS.profile.phoneHome.required,
  [PROFILE_FIELDS_TYPES.HOME_PHONE]: VALIDATION_ERRORS.profile.phoneHome.format,
};

const commonRequiredFields = [
  [PROFILE_FIELDS_TYPES.FIRST_NAME],
  [PROFILE_FIELDS_TYPES.LAST_NAME],
  [PROFILE_FIELDS_TYPES.DOB],
  [PROFILE_FIELDS_TYPES.HOME_PHONE],
];

const extraRequiredFields = [
  [PROFILE_FIELDS_TYPES.GENDER],
  [PROFILE_FIELDS_TYPES.ADDRESS_1],
  [PROFILE_FIELDS_TYPES.CITY],
  [PROFILE_FIELDS_TYPES.STATE],
  [PROFILE_FIELDS_TYPES.COUNTRY],
];

const eirVisitTypeRequiredFields = [
  [PROFILE_FIELDS_TYPES.RACE],
  [PROFILE_FIELDS_TYPES.ETHNICITY],
  [PROFILE_FIELDS_TYPES.LANGUAGE],
];

export const validateRequiredFields = (
  isZipApplicable = true,
  isInlineVisitType,
  isEirVisitType,
) => {
  return isInlineVisitType
    ? makeRequiredFieldsValidator([...commonRequiredFields])
    : makeRequiredFieldsValidator([
        ...commonRequiredFields,
        ...extraRequiredFields,
        ...(isZipApplicable && [PROFILE_FIELDS_TYPES.POSTAL_CODE]),
        ...(isEirVisitType && eirVisitTypeRequiredFields),
      ]);
};
function makePopulatedObject(source, isInlineVisitType, isEirVisitType) {
  return mapValues(
    getInitialState(isInlineVisitType, isEirVisitType),
    (value, key) => get(source, key, ''),
  );
}

export function usePatientForm(
  errors,
  originData,
  savedFormValues,
  isZipApplicable,
  isInlineVisitType,
  isEirVisitType,
  homeAddressSectionInitialValue,
) {
  const filledKeys = useMemo(
    () =>
      keys(
        omitBy(
          makePopulatedObject(originData, isInlineVisitType, isEirVisitType),
          isEmpty,
        ),
      ),
    [originData, isInlineVisitType, isEirVisitType],
  );

  const state = useMemo(
    () =>
      makePopulatedObject(
        savedFormValues || originData,
        isInlineVisitType,
        isEirVisitType,
      ),
    [originData, savedFormValues],
  );

  const {
    validateErrors,
    clearErrors,
    addLocalValidateErrors,
  } = useFormValidation(errors);

  const initialFormState = useMemo(
    () => ({
      ...getInitialState(isInlineVisitType, isEirVisitType),
      ...state,
      ...homeAddressSectionInitialValue,
    }),
    [isInlineVisitType, isEirVisitType, state, homeAddressSectionInitialValue],
  );

  const { formState, handleFormChange } = useFormState(initialFormState);

  const isValid = validateRequiredFields(
    isZipApplicable,
    isInlineVisitType,
    isEirVisitType,
  )(formState);

  function isDisabled(field) {
    return filledKeys.includes(field);
  }

  const setCountryValue = value =>
    handleFormChange({
      ...formState,
      [PROFILE_FIELDS_TYPES.COUNTRY]: value,
      [PROFILE_FIELDS_TYPES.STATE]: '',
      [PROFILE_FIELDS_TYPES.POSTAL_CODE]: '',
    });

  return {
    validateErrors,
    clearErrors,
    formState,
    isDisabled,
    addLocalValidateErrors,
    handleFormChange,
    isValid,
    setCountryValue,
  };
}

function useFormValidation(errors) {
  const [validateErrors, setValidateErrors] = useState({});
  const errorsMsg = errors.reduce((acc, item) => {
    const defaultError = [{ id: 'errors.validation.common' }];
    const key = camelCase(get(item, 'key', null));
    acc[key] = [msg[key]] || defaultError;
    return acc;
  }, {});

  useEffect(() => {
    if (errors) {
      setValidateErrors(errorsMsg);
    }
  }, [errors]);

  function clearErrors() {
    setValidateErrors({});
  }

  return { validateErrors, clearErrors };
}



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentPatientInfo/PatientForm/usePatientForm.js